import { enumeratedBodies } from './bodyPerfection.js'
import { bodyRecordReadiness } from './bodyDateEvidence.js'

const text = value => String(value ?? '').trim()
const normalized = value => text(value).toLowerCase()
const readiness = () => ({ active: 0, inactive: 0, unassigned: 0, owners: {}, dates: {} })
function addReadiness(totals, item) {
  const row = bodyRecordReadiness(item)
  if (row.inactive) { totals.inactive++; return }
  totals.active++
  if (!row.owner) totals.unassigned++
  else totals.owners[row.owner] = (totals.owners[row.owner] || 0) + 1
  totals.dates[row.date] = (totals.dates[row.date] || 0) + 1
}

// The directory is the independent starting set. A body with no CRM records
// must remain visible. This measures the KNOWN directory, not its completeness
// against every official governing-body directory or every seasonal title.
export function bodyMarketInventory({ records, registry, ledger, resolveCard }) {
  if (!Array.isArray(records) || !Array.isArray(registry) || typeof resolveCard !== 'function') {
    throw Error('The complete record set, governing-body registry and resolver are required.')
  }
  const bodies = new Map(), byKey = new Map()
  for (const entry of registry) {
    const id = normalized(entry.name), key = text(entry.key)
    if (!id || !key) throw Error('A governing-body registry entry has no name or key.')
    if (!bodies.has(id)) bodies.set(id, { id, name: text(entry.name), cardKeys: [],
      records: 0, outOfScope: 0, enumeration: [], readiness: readiness() })
    const body = bodies.get(id)
    if (byKey.has(key) && byKey.get(key) !== body) throw Error('A registry key identifies different organizations.')
    if (!body.cardKeys.includes(key)) body.cardKeys.push(key)
    byKey.set(key, body)
  }
  const bodyFor = label => {
    if (!text(label)) return null
    const card = resolveCard(label)
    return card ? byKey.get(text(card.key)) || null : null
  }
  const unlinkedEnumeration = []
  for (const entry of enumeratedBodies(ledger || { bodies: [] })) {
    const matches = new Set([entry.name, ...(entry.gb || [])].map(bodyFor).filter(Boolean))
    if (matches.size !== 1) { unlinkedEnumeration.push({ name: entry.name, source: entry.source || null }); continue }
    const [body] = matches
    body.enumeration.push({ source: entry.source || null,
      publishedTitleCount: Number.isSafeInteger(entry.official) && entry.official >= 0 ? entry.official : null,
      season: text(entry.season) || null })
  }
  const unmatched = new Map()
  const allReadiness = readiness()
  let unattributed = 0, conflicts = 0, matchedRecords = 0
  for (const item of records) {
    addReadiness(allReadiness, item)
    const row = item.raw || item
    const governing = text(row.governing_body), conference = text(row.sr_conference)
    const label = governing || conference
    if (!label || /^(unspecified|unknown|none)$/i.test(label)) { unattributed++; continue }
    const body = bodyFor(label), other = governing && conference ? bodyFor(conference) : null
    if (body && other && body !== other) { conflicts++; continue }
    if (!body) {
      if (!unmatched.has(label)) unmatched.set(label, { name: label, count: 0, readiness: readiness() })
      const candidate = unmatched.get(label); candidate.count++; addReadiness(candidate.readiness, item)
      continue
    }
    body.records++
    addReadiness(body.readiness, item)
    matchedRecords++
    if (row.sr_scope_status === 'Out of Scope') body.outOfScope++
  }
  const rows = [...bodies.values()].map(body => ({ ...body,
    unmeasured: body.enumeration.length === 0,
    noHoldings: body.readiness.active === 0,
  })).sort((a, b) => Number(b.noHoldings) - Number(a.noHoldings) || a.name.localeCompare(b.name))
  const unmatchedRecords = [...unmatched.values()].reduce((a, b) => a + b.count, 0)
  return { rows, unmatched: [...unmatched.values()], unlinkedEnumeration, readiness: allReadiness,
    summary: { knownRecords: records.length, registryCardRows: registry.length, registryOrganizations: rows.length,
      noHoldings: rows.filter(row => row.noHoldings).length,
      withoutEnumeration: rows.filter(row => row.unmeasured).length,
      matchedRecords, unmatchedRecords, unattributedRecords: unattributed, conflictingRecords: conflicts,
      accountedRecords: matchedRecords + unmatchedRecords + unattributed + conflicts,
      reconciles: matchedRecords + unmatchedRecords + unattributed + conflicts === records.length,
      expectedSeasonalOpportunities: null, marketComplete: false,
      completenessReason: 'The known registry and count ledger do not independently certify all governing bodies or a season-specific list of every championship.' } }
}
